
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src2815122270/src/vl-app/pages/clinician/reports.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@actions';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import { useQuery } from '@tanstack/react-query';
import { Tabs } from 'antd';
import { css } from '@emotion/react';
import { models } from 'powerbi-client';
import Header from '@components/Layouts/Header/Header';

const PageHead = dynamic(() => import('@components/Layouts/PageHead/PageHead'));
// const PowerBIEmbedded = dynamic(() => import('nextjs-powerbi'), { ssr: false });

// React.lazy requires a default export, so we map the named export to 'default'
const PowerBIEmbedded = React.lazy(() =>
  import('powerbi-client-react').then((module) => ({ default: module.PowerBIEmbed }))
);

const POWER_BI_EMBED_BASE_CONFIG = {
  type: 'report',
  embedUrl: 'https://app.powerbi.com/reportEmbed',
  tokenType: models.TokenType.Embed,
  settings: {
    panes: {
      filters: {
        expanded: false,
        visible: false
      }
    }
  }
};

const Reports = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const initialised = useInitialisedStore();
  const { user } = useSelector((state) => state.auth);

  const fetchReports = async () => {
    const fetchReportGroups = async () => {
      const reportGroups = await dispatch(actions.getReportGroups());

      return reportGroups;
    };

    const reportGroups = await fetchReportGroups();

    const fetchReports = async () => {
      const reports = await Promise.all(
        reportGroups.groups.map(({ dataset_id, report_id }) => dispatch(actions.getReport(report_id, dataset_id)))
      );

      return reports;
    };

    const reports = await fetchReports();
    return reports.map((report, i) => ({
      groupCode: reportGroups.groups[i].group_code,
      token: report?.token,
      datasetId: reportGroups.groups[i].dataset_id,
      reportId: reportGroups.groups[i].report_id,
      description: reportGroups.groups[i].description
    }));
  };

  const {
    status: reportsStatus,
    isLoading: reportsLoading,
    data: reports
  } = useQuery({
    queryKey: ['reports'],
    queryFn: fetchReports,
    staleTime: Infinity,
    enabled: initialised,
    refetchInterval: Infinity
  });

  const target = {
    table: 'Clinician Number',
    column: 'Clinician Email'
  };

  const emailFilterConfig = {
    $schema: 'http://powerbi.com/product/schema#basic', //NOSONAR
    filterType: 1,
    target,
    operator: 'In',
    values: [user?.email],
    requireSingleSelection: false
  };

  const target2 = {
    table: 'Clinician',
    column: 'Clinician Email'
  };

  const emailFilterConfig2 = {
    $schema: 'http://powerbi.com/product/schema#basic', //NOSONAR
    filterType: 1,
    target: target2,
    operator: 'In',
    values: [user?.email],
    requireSingleSelection: false
  };

  const target3 = {
    table: 'Clinical Lead',
    column: 'Lead Clinician Email'
  };

  const emailFilterConfig3 = {
    $schema: 'http://powerbi.com/product/schema#basic', //NOSONAR
    filterType: 1,
    target: target3,
    operator: 'In',
    values: [user?.email],
    requireSingleSelection: false
  };

  const slicers = [
    {
      selector: {
        $schema: 'http://powerbi.com/product/schema#slicerTargetSelector', //NOSONAR
        target: target2
      },
      state: {
        filters: [emailFilterConfig2]
      }
    },
    {
      selector: {
        $schema: 'http://powerbi.com/product/schema#slicerTargetSelector', //NOSONAR
        target
      },
      state: {
        filters: [emailFilterConfig]
      }
    },
    {
      selector: {
        $schema: 'http://powerbi.com/product/schema#slicerTargetSelector', //NOSONAR
        target: target3
      },
      state: {
        filters: [emailFilterConfig3]
      }
    }
  ];

  if (!initialised || !reports) {
    return <p>{t('Initialising ...')}</p>;
  }

  const WrappedReports = () => {
    if (!reports) return null;
    if (reportsLoading) return <p>{t('Loading reports ...')}</p>;
    if (reportsStatus === 'error') return <p>{t('Failed to load reports')}</p>;
    if (reportsStatus !== 'success') return null;

    if (reports.length > 1)
      return (
        <Tabs
          items={reports.map(({ groupCode, reportId, token, description }) => ({
            label: description,
            key: groupCode,
            children: (
              <PowerBIEmbedded
                cssClassName="power-bi-embed"
                embedConfig={{
                  ...POWER_BI_EMBED_BASE_CONFIG,
                  id: reportId,
                  accessToken: token,
                  slicers
                }}
              />
            )
          }))}
        />
      );

    return (
      <PowerBIEmbedded
        cssClassName="power-bi-embed"
        embedConfig={{
          ...POWER_BI_EMBED_BASE_CONFIG,
          id: reports?.[0].reportId,
          accessToken: reports?.[0].token,
          slicers
        }}
      />
    );
  };

  return (
    <>
      <Header pageType="clinician" pageTitle="Reports" />

      <section
        className="section-layout"
        css={css`
          .power-bi-embed {
            width: 100%;
            height: 900px;
          }

          iframe {
            border: 0;
            padding-bottom: 2em;

            @media screen and (max-width: 768px) {
              padding-bottom: 6.5em;
            }
          }
        `}
      >
        <PageHead backText="Clinician Report" />

        <main className="container" id="page-content">
          <WrappedReports />
        </main>
      </section>
    </>
  );
};

export default Reports;
export const Route = createFileRoute('/clinician/reports')({ component: Reports });
